/**
 * 로그인/소셜 연동 API 에러에서 사용자에게 보여줄 메시지를 추출·정규화합니다.
 * - 백엔드가 500으로 래핑해도 "이미 가입된 이메일" 등 메시지가 있으면 그대로 노출
 * - status code만 보이거나 메시지가 없으면 친절한 문구로 대체
 */
export type LoginErrorLike = {
  response?: {
    status?: number
    data?: { message?: string; error?: string; detail?: string } | string
  }
  message?: string
  cause?: unknown
  originalError?: unknown
}

const MESSAGE_ALREADY_EXISTS =
  '이미 가입된 이메일입니다. 기존 계정으로 로그인해 주세요.'

/** 게스트 → 소셜 연동 시 해당 소셜 계정이 이미 존재할 때 표시 메시지 */
export const MESSAGE_GUEST_LINK_ALREADY_EXISTS =
  '기존 존재하는 계정입니다. 다른 계정으로 연동해주세요.'
export const MESSAGE_GUEST_LINK_ALREADY_EXISTS_EN =
  'This account already exists. Please link with a different account.'

const MESSAGE_SERVER_ERROR =
  '서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.'

/** 사용자가 소셜 로그인(Apple·구글 등)을 취소했을 때 공통 문구 */
export const MESSAGE_LOGIN_CANCELLED = '로그인이 취소되었습니다.'

/**
 * Apple 네이티브(WebView 브릿지) 또는 ASAuthorization 오류 객체가
 * "사용자 취소"인지 판별합니다. 취소인데도 message만 길게 오는 경우가 있어
 * Web 쪽에서 한 번 더 정규화합니다.
 */
export function isAppleUserCancellationError(err: unknown): boolean {
  if (err == null) return false
  const e = err as { message?: string; code?: string | number }
  const msg = typeof e.message === 'string' ? e.message : ''
  if (msg === MESSAGE_LOGIN_CANCELLED) return true
  const msgLower = msg.toLowerCase()
  const codeStr = e.code !== undefined && e.code !== null ? String(e.code) : ''
  return (
    codeStr === '1001' ||
    e.code === 'ERR_REQUEST_CANCELED' ||
    codeStr === 'ERR_REQUEST_CANCELED' ||
    msgLower.includes('canceled') ||
    msgLower.includes('cancelled') ||
    /authorizationerror\s*error\s*1001/i.test(msg)
  )
}

function parseStringPayloadMessage(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  if (!(trimmed.startsWith('{') && trimmed.endsWith('}'))) return ''
  try {
    const parsed = JSON.parse(trimmed) as { message?: string; error?: string; detail?: string }
    return (
      (typeof parsed.message === 'string' && parsed.message.trim()) ||
      (typeof parsed.error === 'string' && parsed.error.trim()) ||
      (typeof parsed.detail === 'string' && parsed.detail.trim()) ||
      ''
    )
  } catch {
    return ''
  }
}

function pickDataMessage(data: LoginErrorLike['response'] extends { data?: infer T } ? T : unknown): string {
  if (typeof data === 'string') {
    return parseStringPayloadMessage(data) || data.trim()
  }
  if (!data || typeof data !== 'object') return ''
  const obj = data as { message?: string; error?: string; detail?: string }
  return (
    (typeof obj.message === 'string' && obj.message.trim()) ||
    (typeof obj.error === 'string' && obj.error.trim()) ||
    (typeof obj.detail === 'string' && obj.detail.trim()) ||
    ''
  )
}

function extractMessage(err: LoginErrorLike): string {
  const nestedErrors = [err, err?.cause as LoginErrorLike, err?.originalError as LoginErrorLike].filter(
    Boolean
  ) as LoginErrorLike[]

  // 1) 서버 응답 payload(message/error/detail) 우선
  for (const e of nestedErrors) {
    const fromData = pickDataMessage(e?.response?.data)
    if (fromData) return fromData
  }

  // 2) message가 JSON 문자열인 경우 내부 message 추출
  for (const e of nestedErrors) {
    const rawMessage = typeof e?.message === 'string' ? e.message.trim() : ''
    if (!rawMessage) continue
    const parsed = parseStringPayloadMessage(rawMessage)
    if (parsed) return parsed
  }

  // 3) 일반 message는 마지막 fallback (Network Error 등이 여기로 옴)
  for (const e of nestedErrors) {
    const rawMessage = typeof e?.message === 'string' ? e.message.trim() : ''
    if (rawMessage) return rawMessage
  }
  return ''
}

function looksLikeStatusOnly(msg: string): boolean {
  const t = msg.trim()
  return (
    t === '500' ||
    t === '400' ||
    t === '409' ||
    t === '401' ||
    t.toLowerCase() === 'internal server error' ||
    /^request failed with status code \d+$/i.test(t)
  )
}

function isAlreadyExistsMessage(msg: string): boolean {
  return (
    /이미\s*가입된|이미\s*존재하는|기존\s*존재하는\s*계정|already\s*exists|EMAIL_ALREADY_EXISTS|account\s*exists|duplicate\s*entry/i.test(
      msg
    )
  )
}

/**
 * 로그인/토큰 교환 실패 시 토스트·UI에 쓸 메시지를 반환합니다.
 * @param err API 에러 (axios 등)
 * @param fallback 메시지를 추출할 수 없을 때 사용할 기본 문구
 * @param isGuestLinking 게스트 계정에서 소셜 계정을 연동하는 과정인지 여부
 */
export function getLoginErrorMessage(
  err: LoginErrorLike,
  fallback: string = '로그인에 실패했습니다. 다시 시도해 주세요.',
  isGuestLinking: boolean = false
): string {
  const msg = extractMessage(err)
  const status = err?.response?.status

  if (isAlreadyExistsMessage(msg)) {
    return isGuestLinking ? MESSAGE_GUEST_LINK_ALREADY_EXISTS : MESSAGE_ALREADY_EXISTS
  }
  if (status === 500 || status === 502 || status === 503) {
    if (!msg || looksLikeStatusOnly(msg)) {
      return MESSAGE_SERVER_ERROR
    }
    return msg
  }
  if (msg && !looksLikeStatusOnly(msg)) {
    return msg
  }
  return fallback
}
